import { Component, OnInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { ImageViewerConfig } from 'ngx-image-viewer';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Select } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { DuplicateSuspicionService } from '../_services/duplicate-suspicion.service';
import {
    DuplicateSuspicion,
    DuplicateSuspicionStatus,
    DuplicateExpenseSummary,
} from '../_models/DuplicateSuspicion';
import { CompanyState } from '../../../shared/stores/company/company.state';
import { Company } from '../../../shared/_models/Company';
import { ReportsService } from '../../../shared/stores/reports/reports.service';
import { AnalyticsService } from '../../../shared/_services/analytics.service';

export interface ReportGroup {
    reportId: number;
    reportName: string;
    reportStateCode: string;
    userDisplayName: string;
    userEmail: string;
    suspicions: DuplicateSuspicion[];
}

@Component({
    selector: 'on-duplicate-suspicions-page',
    templateUrl: './duplicate-suspicions-page.component.html',
    styleUrls: ['./duplicate-suspicions-page.component.scss'],
})
export class DuplicateSuspicionsPageComponent implements OnInit, OnDestroy {
    @Select(CompanyState.current) currentCompany$: Observable<Company>;
    @ViewChild('compareModal') compareModalTemplate: TemplateRef<any>;

    isLoading = true;
    reportGroups: ReportGroup[] = [];
    selectedStatus: DuplicateSuspicionStatus | '' = '';
    companyId: number;
    compareModalRef: NgbModalRef | null = null;

    // Compare modal
    compareSuspicion: DuplicateSuspicion | null = null;
    compareExpenseA: DuplicateExpenseSummary | null = null;
    compareExpenseB: DuplicateExpenseSummary | null = null;
    imageConfig: ImageViewerConfig = {
        btnClass: 'default',
        zoomFactor: 0.1,
        containerBackgroundColor: '#FFF',
        wheelZoom: true,
        allowFullscreen: false,
        allowKeyboardNavigation: false,
        btnIcons: {
            zoomIn: 'fas fa-plus',
            zoomOut: 'fas fa-minus',
            rotateClockwise: 'fas fa-redo',
            rotateCounterClockwise: 'fas fa-undo',
            next: 'fas fa-arrow-right',
            prev: 'fas fa-arrow-left',
            fullscreen: 'fas fa-arrows-alt',
        },
        btnShow: {
            zoomIn: true,
            zoomOut: true,
            rotateClockwise: true,
            rotateCounterClockwise: true,
            next: false,
            prev: false,
        },
    };

    private destroy$ = new Subject<void>();

    constructor(
        private duplicateSuspicionService: DuplicateSuspicionService,
        private reportsService: ReportsService,
        private translate: TranslateService,
        private modalService: NgbModal,
        private analytics: AnalyticsService,
    ) {}

    ngOnInit() {
        this.currentCompany$
            .pipe(takeUntil(this.destroy$))
            .subscribe((company) => {
                if (company) {
                    this.companyId = company.Id;
                    this.loadSuspicions();
                }
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadSuspicions() {
        this.isLoading = true;
        const status = this.selectedStatus || undefined;
        this.duplicateSuspicionService
            .getByCompany(this.companyId, status)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                if (response.IsSuccess) {
                    this.buildReportGroups(response.Result);
                }
                this.isLoading = false;
            });
    }

    private buildReportGroups(suspicions: DuplicateSuspicion[]) {
        const groupMap = new Map<number, ReportGroup>();

        for (const s of suspicions) {
            const reportId = s.Expense?.ReportId;
            if (!reportId) continue;

            if (!groupMap.has(reportId)) {
                const exp = s.Expense;
                const fullName = [exp.UserFirstName, exp.UserLastName]
                    .filter(Boolean)
                    .join(' ');
                groupMap.set(reportId, {
                    reportId,
                    reportName: exp.ReportName || `#${reportId}`,
                    reportStateCode: exp.ReportStateCode || '',
                    userDisplayName: fullName,
                    userEmail: exp.UserName || '',
                    suspicions: [],
                });
            }
            groupMap.get(reportId).suspicions.push(s);
        }

        this.reportGroups = Array.from(groupMap.values());
    }

    onStatusFilterChange() {
        this.loadSuspicions();
    }

    getPendingCount(group: ReportGroup): number {
        return group.suspicions.filter((s) => s.Status === 'Pending').length;
    }

    allReviewed(group: ReportGroup): boolean {
        return group.suspicions.every((s) => s.Status !== 'Pending');
    }

    hasConfirmed(group: ReportGroup): boolean {
        return group.suspicions.some((s) => s.Status === 'Confirmed');
    }

    getConfidencePercent(confidence: number): string {
        return Math.round(confidence * 100) + '%';
    }

    getConfidenceBarClass(confidence: number): string {
        if (confidence >= 0.8) return 'bg-danger';
        if (confidence >= 0.6) return 'bg-warning';
        return 'bg-info';
    }

    getStatusBadgeClass(status: DuplicateSuspicionStatus): string {
        switch (status) {
            case 'Pending':
                return 'bg-warning text-dark';
            case 'Confirmed':
                return 'bg-danger';
            case 'Dismissed':
                return 'bg-success';
            default:
                return 'bg-secondary';
        }
    }

    isReportAlreadyRefused(group: ReportGroup): boolean {
        return group.reportStateCode !== 'VAL' && group.reportStateCode !== 'ARCHIVED';
    }

    private updateStatus(
        suspicion: DuplicateSuspicion,
        status: DuplicateSuspicionStatus,
    ) {
        this.duplicateSuspicionService
            .updateStatus(suspicion.Id, status)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                if (response.IsSuccess) {
                    suspicion.Status = response.Result.Status;
                    suspicion.ReviewedDate = response.Result.ReviewedDate;
                    suspicion.ReviewedByUserId =
                        response.Result.ReviewedByUserId;
                    this.trackReview(suspicion, status);
                }
            });
    }

    private trackReview(
        suspicion: DuplicateSuspicion,
        status: DuplicateSuspicionStatus,
    ) {
        const properties = {
            suspicion_id: suspicion.Id,
            confidence: suspicion.Confidence,
            expense_id: suspicion.ExpenseId,
            suspected_duplicate_expense_id:
                suspicion.SuspectedDuplicateExpenseId,
            report_id: suspicion.Expense?.ReportId ?? undefined,
            company_id: this.companyId,
        };
        if (status === 'Confirmed') {
            this.analytics.trackDuplicateConfirmed(properties);
        } else if (status === 'Dismissed') {
            this.analytics.trackDuplicateDismissed(properties);
        }
    }

    openCompare(suspicion: DuplicateSuspicion) {
        this.compareSuspicion = suspicion;
        this.compareExpenseA = suspicion.Expense;
        this.compareExpenseB = suspicion.SuspectedDuplicate;
        this.compareModalRef = this.modalService.open(this.compareModalTemplate, {
            size: 'xl',
            centered: true,
        });
    }

    confirmFromModal() {
        if (!this.compareSuspicion) return;
        this.updateStatus(this.compareSuspicion, 'Confirmed');
        this.compareModalRef?.close();
    }

    dismissFromModal() {
        if (!this.compareSuspicion) return;
        this.updateStatus(this.compareSuspicion, 'Dismissed');
        this.compareModalRef?.close();
    }

    sendBackReport(group: ReportGroup) {
        const confirmed = group.suspicions.filter(
            (s) => s.Status === 'Confirmed',
        );

        const commentLines = confirmed.map((s) =>
            this.translate.instant('company.duplicates.refuse-comment-line', {
                emitter:
                    s.Expense.Emitter || s.SuspectedDuplicate.Emitter || '',
                amount: s.Expense.TotalTtc.toFixed(2),
                currency: s.Expense.CurrencySymbol || s.Expense.CurrencyCode || '€',
            }),
        );
        commentLines.push('');
        commentLines.push(
            this.translate.instant('company.duplicates.refuse-comment-footer'),
        );

        Swal.fire({
            title: this.translate.instant(
                'company.duplicates.send-back-title',
            ),
            input: 'textarea',
            inputValue: commentLines.join('\n'),
            inputLabel: this.translate.instant(
                'company.duplicates.send-back-comment-label',
            ),
            showCancelButton: true,
            confirmButtonText: this.translate.instant(
                'company.duplicates.send-back-confirm',
            ),
            cancelButtonText: this.translate.instant(
                'company.duplicates.cancel',
            ),
            customClass: {
                confirmButton: 'btn btn-danger m-1',
                cancelButton: 'btn btn-secondary m-1',
            },
            buttonsStyling: false,
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.translate.instant(
                        'company.duplicates.send-back-loading',
                    ),
                    allowOutsideClick: false,
                    didOpen: () => Swal.showLoading(),
                });
                this.reportsService
                    .Refuse(group.reportId, result.value)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((response) => {
                        if (response.IsSuccess) {
                            Swal.fire({
                                title: this.translate.instant(
                                    'company.duplicates.send-back-success',
                                ),
                                icon: 'success',
                                timer: 2000,
                                showConfirmButton: false,
                            });
                            this.loadSuspicions();
                        } else {
                            Swal.fire({
                                title: this.translate.instant(
                                    'company.duplicates.send-back-error',
                                ),
                                icon: 'error',
                            });
                        }
                    });
            }
        });
    }

    reload() {
        this.loadSuspicions();
    }
}
